import { Box, FormControl, TextInput } from "@primer/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { phuHuynhApi } from "../../../../api/phuHuynhApi";
import { quanHeGiaDinhApi } from "../../../../api/quanHeGiaDinhApi";
import ComboboxQuanHe from "../../../../components/combobox-quanhe/ComboboxQuanHe";
import ComboboxQuocTich from "../../../../components/combobox-quoctich";
import MyButton from "../../../../components/ui/button/Button";
import Modal from "../../../../components/ui/modal";
import MyTextInput from "../../../../components/ui/text-input/TextInput";
import { useCommonContext } from "../../../../contexts/common";
import { NotifyHelper } from "../../../../helpers/toast";
import { useCommonSelectedHook } from "../../../../hooks/useCommonSelectedHook";
import { PhuHuynhQuanHeGiaDinhItemResponse } from "../../../../models/response/phu-huynh/PhuHuynhQuanHeGiaDinhItemResponse ";
import SearchBoxPhuHuynh from "../../../../components/searchbox-phuhuynh";
import { IPhuHuynhItemRespone } from "../../../../models/response/phu-huynh/IPhuHuynhItemRespone";
import ComboBoxPhuHuynh from "../../../../components/searchbox-phuhuynh/ComboBoxPhuHuynh";

interface QuanHeGiaDinhEditModalProps {
  ts_hocsinh_id: number;
  id: number;
  onCancel: () => void;
  onSuccess: () => void;
}

const QuanHeGiaDinhEditModal: React.FC<QuanHeGiaDinhEditModalProps> = ({
  ts_hocsinh_id,
  id,
  onCancel,
  onSuccess,
}) => {
  console.log("QuanHeGiaDinhEditModal props:", { ts_hocsinh_id, id });
  const [formData, setFormData] = useState<PhuHuynhQuanHeGiaDinhItemResponse>({
    id: 0,
    ho_ten: "",
    dien_thoai: "",
    email: "",
    dm_gioitinh_id: 0,
    dm_quanhe_id: 0,
    dia_chi_thuong_tru: "",
    chung_minh_thu: "",
    nghe_nghiep: "",
    noi_lam_viec: "",
    avatar: "",
    ts_phuhuynh_adm_status_id: 0,
    adm_ghi_chu: "",
    dm_chiendich_id: 0,
    adm_nguon_tuyen_sinh: "",
    user_tu_van_id: 0,
    dm_coso_id: 0,
    ngay_sinh: undefined,
    dm_quoctich_id: 0,
    noi_cap_chung_minh_thu: "",
    ngay_cap_chung_minh_thu: undefined,
    is_uutien: false,
    ts_phuhuynh_id: 0,
  });

  const [isSaving, setIsSaving] = useState(false);
  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const [state, setState] = useState(formData);

  const [phoneExistsError, setPhoneExistsError] = useState<string | null>(null);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const phoneCheckTimeoutRef = useRef<number | null>(null);

  const checkPhoneExists = useCallback(async (phone: string) => {
    if (!phone || phone.trim().length < 8) {
      setPhoneExistsError(null);
      return;
    }
    setIsCheckingPhone(true);
    try {
      const res = await phuHuynhApi.CheckPhone(phone.trim(), formData.ts_phuhuynh_id || 0);
      if (res.is_success && res.data?.is_existed) {
        setPhoneExistsError("Số ĐT đã tồn tại, vui lòng nhấn \"Tìm phụ huynh\"");
      } else {
        setPhoneExistsError(null);
      }
    } catch (error) {
      console.error("Error checking phone:", error);
      setPhoneExistsError(null);
    } finally {
      setIsCheckingPhone(false);
    }
  }, [formData.ts_phuhuynh_id]);

  const debouncedCheckPhone = useCallback((phone: string) => {
    if (phoneCheckTimeoutRef.current) {
      window.clearTimeout(phoneCheckTimeoutRef.current);
    }
    phoneCheckTimeoutRef.current = window.setTimeout(() => {
      checkPhoneExists(phone);
    }, 500);
  }, [checkPhoneExists]);

  const handleGetDetail = async () => {
    if (id > 0) {
      try {
        const res = await phuHuynhApi.SelectByHocSinhQuanHeGiaDinh(
          ts_hocsinh_id,
          id
        );

        if (res.is_success && res.data) {
          const apiData = res.data;
          const newFormData = {
            ...apiData,
            ngay_cap_chung_minh_thu: apiData.ngay_cap_chung_minh_thu
              ? new Date(apiData.ngay_cap_chung_minh_thu)
              : undefined,
            ngay_sinh: apiData.ngay_sinh
              ? new Date(apiData.ngay_sinh)
              : undefined,
            is_uutien: Boolean(apiData.is_uutien) || false,
            ts_phuhuynh_id: apiData.ts_phuhuynh_id || 0,
          };
          setFormData(newFormData);
        }
      } catch (error) {
        console.error("Error fetching detail:", error);
        NotifyHelper.Error("Có lỗi khi tải dữ liệu");
      }
    }
  };

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setError,
    control,
    watch,
    trigger,
    formState: { errors },
  } = useForm<PhuHuynhQuanHeGiaDinhItemResponse>({
    mode: "all",
    defaultValues: formData,
  });

  useEffect(() => {
    handleGetDetail();
  }, [ts_hocsinh_id, id]);

  // Reset form when formData changes
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      reset(formData);
    }
  }, [formData, reset]);

  const handleSaveChanges = async (data: any) => {
    try {
      if (data.dm_quanhe_id === undefined || data.dm_quanhe_id === 0) {
        NotifyHelper.Error("Vui lòng chọn quan hệ");
        return;
      }
      if (phoneExistsError) {
        NotifyHelper.Error(phoneExistsError);
        return;
      }
      setIsSaving(true);
      const formDataSubmit = {
        ...formData,
        ts_hocsinh_id,
        dm_coso_id,
        ts_hoso_survey_id: 0,
        ts_hocsinh_ace_id: 0,
        ts_phuhuynh_id: data.ts_phuhuynh_id ?? 0,
        is_uutien: data.is_uutien,
        dm_quanhe_id: data.dm_quanhe_id,
        ho_ten: data.ho_ten,
        dien_thoai: data.dien_thoai,
        email: data.email || "",
        dm_quoctich_id: data.dm_quoctich_id,
        chung_minh_thu: data.chung_minh_thu,
        ngay_cap_chung_minh_thu: data.ngay_cap_chung_minh_thu,
        noi_cap_chung_minh_thu: data.noi_cap_chung_minh_thu,
        dia_chi_thuong_tru: data.dia_chi_thuong_tru,
        nghe_nghiep: data.nghe_nghiep,
        noi_lam_viec: data.noi_lam_viec,
      };

      const isValid = await trigger();
      if (!isValid) {
        setIsSaving(false);
        return;
      }

      try {
        const res = await (formDataSubmit.id > 0
          ? quanHeGiaDinhApi.update(formDataSubmit)
          : quanHeGiaDinhApi.insert(formDataSubmit));

        if (res.is_success) {
          NotifyHelper.Success("Cập nhật thành công");
          onSuccess();
        } else {
          const errorMessage = res.message || "Có lỗi khi lưu dữ liệu";
          NotifyHelper.Error(errorMessage);
        }
      } catch (error) {
        console.error("Error saving:", error);
        NotifyHelper.Error("Có lỗi khi lưu dữ liệu");
      }
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    setState(formData);
  }, [formData]);

  return (
    <Modal
      isOpen
      onClose={onCancel}
      width="xlarge"
      shouldCloseOnOverlayClick={false}
      title={id > 0 ? translate("Base.Label.Edit") : translate("Base.Label.AddNew")}
    >
      <Box
        className="bottom-side"
        overflow="hidden"
      // height={props.height ?? window.innerHeight - 300}
      >
        <form onSubmit={handleSubmit(handleSaveChanges)}>
          <Box className="content">
            <Box p={0}>
              <Box as="form">
                <div className="row">
                  <div className="col-md-12" style={{ paddingTop: "10px" }}>
                    <div className="row">
                      <div className="col-md-12">
                        <FormControl sx={{ marginBottom: "10px" }}>
                          {/* <SearchBoxPhuHuynh
                            isReadonly={false}
                            onValueChanged={handlePhuHuynhChanged}
                            isShowClearBtn={true}
                            maxWidth="200px"
                            onRowClick={handlePhuHuynhClick}
                          /> */}

                          <ComboBoxPhuHuynh
                            value={formData.ts_phuhuynh_id}
                            dien_thoai={formData.dien_thoai}
                            onValueChanged={(id: number, data?: IPhuHuynhItemRespone) => {
                              setFormData((prev) => ({
                                ...prev,
                                ho_ten: data?.ho_ten ?? "",
                                dien_thoai: data?.dien_thoai ?? "",
                                email: data?.email ?? "",
                                dm_gioitinh_id: data?.dm_gioitinh_id ?? 0,
                                dm_quanhe_id: data?.dm_quanhe_id ?? 0,
                                dia_chi_thuong_tru: data?.dia_chi_thuong_tru ?? "",
                                chung_minh_thu: data?.chung_minh_thu ?? "",
                                nghe_nghiep: data?.nghe_nghiep ?? "",
                                noi_lam_viec: data?.noi_lam_viec ?? "",
                                ts_phuhuynh_id: data?.id
                              }));
                              setPhoneExistsError(null);
                            }}
                            isShowClearBtn={true}
                            isReadonly={false}
                          />
                        </FormControl>
                      </div>
                      <div className="col-md-6">
                        <FormControl sx={{ marginBottom: "10px" }}>
                          <FormControl.Label>Họ tên</FormControl.Label>
                          <MyTextInput
                            block
                            name="ho_ten"
                            register={register}
                            errors={errors}
                            required
                            validateMessage="Vui lòng điền họ tên"
                          />
                        </FormControl>
                      </div>
                      <div className="col-md-6">
                        <FormControl sx={{ marginBottom: "10px" }}>
                          <FormControl.Label>Điện thoại</FormControl.Label>
                          <MyTextInput
                            block
                            name="dien_thoai"
                            register={register}
                            errors={errors}
                            required
                            onValueDelayChanged={(value) => {
                              setFormData((prev) => ({
                                ...prev,
                                dien_thoai: value,
                              }));
                              debouncedCheckPhone(value);
                            }}
                            validateMessage="Vui lòng điền điện thoại"
                          />
                          {phoneExistsError && (
                            <FormControl.Validation variant="error">
                              {phoneExistsError}
                            </FormControl.Validation>
                          )}
                          {isCheckingPhone && (
                            <FormControl.Caption>Đang kiểm tra số điện thoại...</FormControl.Caption>
                          )}
                        </FormControl>
                      </div>
                      <div className="col-md-6">
                        <FormControl sx={{ marginBottom: "10px" }}>
                          <FormControl.Label>Email</FormControl.Label>
                          <MyTextInput
                            block
                            name="email"
                            register={register}
                            errors={errors}
                          />
                        </FormControl>
                      </div>
                      <div className="col-md-6">
                        <FormControl>
                          <FormControl.Label>Chọn quan hệ</FormControl.Label>
                          <Controller
                            name="dm_quanhe_id"
                            control={control}
                            rules={{ required: "Vui lòng chọn quan hệ" }}
                            render={({ field }) => (
                              <ComboboxQuanHe
                                value={field.value}
                                onValueChanged={(value) => {
                                  field.onChange(value);
                                  setState({ ...state, dm_quanhe_id: value });
                                }}
                              />
                            )}
                          />
                          {errors.dm_quanhe_id && (
                            <FormControl.Validation variant="error">
                              {errors.dm_quanhe_id.message}
                            </FormControl.Validation>
                          )}
                        </FormControl>
                      </div>
                      <div className="col-md-6">
                        <FormControl>
                          <FormControl.Label>Chọn quốc tịch</FormControl.Label>
                          <Controller
                            name="dm_quoctich_id"
                            control={control}
                            rules={{ required: "Vui lòng chọn quốc tịch" }}
                            render={({ field }) => (
                              <ComboboxQuocTich
                                value={field.value}
                                onValueChanged={(value) => {
                                  field.onChange(value);
                                  setState({ ...state, dm_quoctich_id: value });
                                }}
                              />
                            )}
                          />
                          {errors.dm_quoctich_id && (
                            <FormControl.Validation variant="error">
                              {errors.dm_quoctich_id.message}
                            </FormControl.Validation>
                          )}
                        </FormControl>
                      </div>

                      <div className="col-md-6">
                        <FormControl sx={{ marginBottom: "10px" }}>
                          <FormControl.Label>CMND/CCCD</FormControl.Label>
                          <MyTextInput
                            block
                            name="chung_minh_thu"
                            register={register}
                            errors={errors}
                            required={false}
                            validateMessage="Vui lòng điền chứng minh thư"
                          />
                        </FormControl>
                      </div>

                      <div className="col-md-6">
                        <FormControl>
                          <FormControl.Label>
                            Ngày cấp CMND/CCCD
                          </FormControl.Label>
                          <Controller
                            name="ngay_cap_chung_minh_thu"
                            control={control}
                            render={({ field }) => (
                              <TextInput
                                block
                                type="date"
                                value={
                                  field.value instanceof Date
                                    ? field.value.toISOString().split("T")[0]
                                    : field.value
                                      ? new Date(field.value)
                                        .toISOString()
                                        .split("T")[0]
                                      : ""
                                }
                                onChange={(e) => {
                                  const date = e.target.value
                                    ? new Date(e.target.value)
                                    : undefined;
                                  field.onChange(date);
                                }}
                                required={false}
                              />
                            )}
                          />
                        </FormControl>
                      </div>

                      <div className="col-md-6">
                        <FormControl sx={{ marginBottom: "10px" }}>
                          <FormControl.Label>
                            Nơi cấp CMND/CCCD
                          </FormControl.Label>
                          <MyTextInput
                            block
                            name="noi_cap_chung_minh_thu"
                            register={register}
                            errors={errors}
                            required={false}
                            validateMessage="Vui lòng điền nơi cấp chứng minh thư"
                          />
                        </FormControl>
                      </div>

                      <div className="col-md-6">
                        <FormControl sx={{ marginBottom: "10px" }}>
                          <FormControl.Label>
                            Địa chỉ thường trú
                          </FormControl.Label>
                          <MyTextInput
                            block
                            name="dia_chi_thuong_tru"
                            register={register}
                            errors={errors}
                            required={false}
                            validateMessage="Vui lòng điền địa chỉ thường trú"
                          />
                        </FormControl>
                      </div>

                      <div className="col-md-6">
                        <FormControl sx={{ marginBottom: "10px" }}>
                          <FormControl.Label>Nghề nghiệp</FormControl.Label>
                          <MyTextInput
                            block
                            name="nghe_nghiep"
                            register={register}
                            errors={errors}
                            required={false}
                            validateMessage="Vui lòng điền nghề nghiệp"
                          />
                        </FormControl>
                      </div>

                      <div className="col-md-12">
                        <FormControl sx={{ marginBottom: "10px" }}>
                          <FormControl.Label>Nơi làm việc</FormControl.Label>
                          <MyTextInput
                            block
                            name="noi_lam_viec"
                            register={register}
                            errors={errors}
                            required={false}
                            validateMessage="Vui lòng điền nơi làm việc"
                          />
                        </FormControl>
                      </div>

                      <div className="col-md-6">
                        <FormControl sx={{ marginBottom: "10px" }}>
                          <FormControl.Label>
                            <Controller
                              name="is_uutien"
                              control={control}
                              render={({ field }) => (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <input
                                    type="checkbox"
                                    checked={field.value}
                                    onChange={(e) => {
                                      field.onChange(e.target.checked);
                                      setState({
                                        ...state,
                                        is_uutien: e.target.checked,
                                      });
                                    }}
                                    style={{ marginRight: "8px" }}
                                  />
                                  Liên hệ chính
                                </Box>
                              )}
                            />
                          </FormControl.Label>
                        </FormControl>
                      </div>
                    </div>
                  </div>
                </div>
              </Box>
            </Box>
          </Box>
          <div
            className="col-md-12 fixed-bottom"
            style={{
              paddingTop: "10px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <MyButton
              text="Base.Label.Save"
              variant="primary"
              type="submit"
              isLoading={isSaving}
              disabled={!!phoneExistsError || isCheckingPhone}
            />
          </div>
        </form>
      </Box>
    </Modal>
  );
};

export default QuanHeGiaDinhEditModal;
